import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress
} from '@mui/material';
import { Add, Delete, ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import api from '../services/api';

export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKey, setNewKey] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const res = await api.get('/apikeys');
      setApiKeys(res.data);
    } catch (err) {
      setError('Erreur lors du chargement des API Keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    setError('');

    try {
      const res = await api.post('/apikeys', {
        name: newKeyName,
        permissions: ['read', 'write', 'admin']
      });
      setNewKey(res.data.key);
      setApiKeys([...apiKeys, res.data]);
      setSuccess('API Key creee avec succes. Copiez-la maintenant, elle ne sera plus affichee.');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la creation');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette API Key ?')) return;
    try {
      await api.delete(`/apikeys/${id}`);
      setApiKeys(apiKeys.filter(k => k._id !== id));
      setSuccess('API Key supprimee');
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoquer cette API Key ?')) return;
    try {
      await api.put(`/apikeys/${id}/revoke`);
      setApiKeys(apiKeys.map(k =>
        k._id === id ? { ...k, isActive: false } : k
      ));
      setSuccess('API Key revoquee');
    } catch (err) {
      setError('Erreur lors de la revocation');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Cle copiee dans le presse-papier');
    setTimeout(() => setSuccess(''), 2000);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setNewKeyName('');
    setNewKey('');
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">API Keys</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenDialog(true)}
        >
          Nouvelle API Key
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light' }}>
        <Typography variant="body2">
          Les API Keys permettent d'authentifier les requetes automatisees (n8n, scripts).
          Utilisez le header <code>X-API-Key: votre_cle</code> dans vos requetes.
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Creee par</TableCell>
              <TableCell>Derniere utilisation</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {apiKeys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Aucune API Key
                </TableCell>
              </TableRow>
            ) : (
              apiKeys.map((apiKey) => (
                <TableRow key={apiKey._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {apiKey.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Creee le {formatDate(apiKey.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {apiKey.permissions?.map((p, i) => (
                      <Chip key={i} label={p} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {apiKey.userId?.email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {apiKey.lastUsedAt ? formatDate(apiKey.lastUsedAt) : 'Jamais'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {apiKey.isActive ? (
                      <Chip label="Active" size="small" color="success" />
                    ) : (
                      <Chip label="Revoquee" size="small" color="error" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {apiKey.isActive && (
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => handleRevoke(apiKey._id)}
                      >
                        Revoquer
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(apiKey._id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {newKey ? 'API Key Creee' : 'Nouvelle API Key'}
        </DialogTitle>
        <DialogContent>
          {newKey ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Copiez cette cle maintenant. Elle ne sera plus affichee.
              </Alert>
              <TextField
                fullWidth
                value={newKey}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={() => copyToClipboard(newKey)}>
                      <ContentCopy />
                    </IconButton>
                  )
                }}
              />
            </Box>
          ) : (
            <TextField
              autoFocus
              fullWidth
              label="Nom de l'API Key"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="n8n-automation"
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>
            {newKey ? 'Fermer' : 'Annuler'}
          </Button>
          {!newKey && (
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating || !newKeyName.trim()}
            >
              {creating ? <CircularProgress size={20} /> : 'Creer'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
